<template>
  <div class="px-3 py-1.5 shadow dark:bg-charcoal-900 flex items-center justify-between">
    <p class="text-sm font-semibold text-charcoal dark:text-gray-100 tracking-wide leading-none">{{config.title}}</p>
    <u-button color="gray" variant="ghost" icon="i-ri-close-line" @click="surfaceEditorStore.closeContextualPanel()" />
  </div>

  <div class="flex-1 overflow-y-auto scrollbar-light dark:scrollbar-dark p-3">
    <p class="text-sm text-gray-600 dark:text-gray-300 mb-5">Choose a color to apply to the selected shape.</p>

    <div class="flex flex-col gap-6">
      <div v-for="group in filteredColorGroups">
        <surface-editor-core-panel-section-heading>{{group.label}}</surface-editor-core-panel-section-heading>

        <div class="grid grid-cols-6 gap-1.5">

          <surface-editor-contextual-panels-color-custom
            v-if="group.key === 'solid' && allowCustom"
            v-model="customColor"
          />


          <core-tooltip
            v-for="color in group.colors"
            :key="color"
            :text="tooltipText(color, group)"
          >
            <div
              class="relative bg-checkerboard w-full h-full ring-1 ring-charcoal-200 dark:ring-charcoal-300 shadow aspect-[1/1] rounded-full hover:border-2 hover:border-white hover:ring-2 hover:ring-charcoal-300 dark:hover:ring-charcoal-300 transition-all"
              :class="[
                {
                  'ring-2 !ring-charcoal border-2 border-white': selectedColor === color
                }
              ]"
            >
              <button
                type="button"
                class="w-full h-full rounded-full"
                :style="surfaceEditorUtils.fillToStyle({fill: color, isButton: true})"
                @click="selectColor(color)"
              >
                <span class="sr-only">Select {{ color }}</span>
              </button>

              <div v-if="color.color === transparentColor.color" class="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div
                  class="w-14 h-0.5 bg-red-500 -rotate-45 rounded-full shadow-sm"
                />
              </div>
            </div>
          </core-tooltip>

        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';

  const props = defineProps({
    current: Object,
    onSelect: {
      type: Function,
      required: true
    },
    mode: String
  });

  const surfaceEditorUtils = useSurfaceEditorUtils();
  const surfaceEditorStore = useSurfaceEditorStore();

  useAsyncData(
    'design-surface-colors',
    () => surfaceEditorStore.getColors()
  );

  const {colorGroups} = storeToRefs(surfaceEditorStore);

  const config = computed(() => {
    switch (props.mode) {
      case SURFACE_EDITOR_COLOR_PANEL_MODES.shapeFill:
      case SURFACE_EDITOR_COLOR_PANEL_MODES.bgFill:
        return {
          title: 'Color',
          types: [
            SURFACE_EDITOR_COLOR_TYPES.solid,
            SURFACE_EDITOR_COLOR_TYPES.linearGradient,
            SURFACE_EDITOR_COLOR_TYPES.imagePattern
          ],
          canBeTransparent: props.mode === SURFACE_EDITOR_COLOR_PANEL_MODES.shapeFill
        };
      case SURFACE_EDITOR_COLOR_PANEL_MODES.shapeStroke:
        return {
          title: 'Stroke Color',
          types: [SURFACE_EDITOR_COLOR_TYPES.solid]
        };
      case SURFACE_EDITOR_COLOR_PANEL_MODES.text:
        return {
          title: 'Text Color',
          types: [SURFACE_EDITOR_COLOR_TYPES.solid]
        };
    }
  });

  const transparentColor = {
    id: 'No color',
    color: 'transparent',
    type: SURFACE_EDITOR_COLOR_TYPES.solid.key
  };

  const colorTypesKeys = computed(() => config.value.types.map(t => t.key));
  const filteredColorGroups = computed(() => colorGroups.value &&
    colorTypesKeys.value.reduce((filtered, key) => {
      if (colorGroups.value[key]) {
        filtered[key] = Object.assign({}, colorGroups.value[key], {
          colors: colorGroups.value[key].colors.slice()
        });
      }

      if (key === SURFACE_EDITOR_COLOR_TYPES.solid.key && config.value.canBeTransparent) {
        filtered[key].colors.unshift(transparentColor);
      }

      return filtered;
    }, {})
  );

  const allowCustom = computed(() => config.value.types.includes(SURFACE_EDITOR_COLOR_TYPES.solid));

  const predefinedSolidColors = computed(() => {
    const solidGroup = colorGroups.value?.[SURFACE_EDITOR_COLOR_TYPES.solid.key];
    const colors = solidGroup?.colors ?? [];
    return new Set(colors.map(c => (typeof c === 'string' ? c : c.color)));
  });

  const isCustomSolidSelected = computed(() => {
    if (!props.current) return false;
    if (props.current.type !== SURFACE_EDITOR_COLOR_TYPES.solid.key || props.current.color === transparentColor.color) return false;
    const c = props.current.color;
    if (!c) return false;
    return !predefinedSolidColors.value.has(c);
  });

  const customColor = computed({
    get() {
      return isCustomSolidSelected.value ? props.current.color : null;
    },
    set(val) {
      if (!val) return;
      selectColor({ type: SURFACE_EDITOR_COLOR_TYPES.solid.key, color: val });
    },
  });

  const selectedColor = computed(() => filteredColorGroups.value && Object.values(filteredColorGroups.value)
    .reduce((all, group) => [...all, ...group.colors], [])
    .find((c) => {
      return props.current && (
        (props.current.color && c.color === props.current.color)
          || (props.current.url && c.url === props.current.url)
          || ((c.angle || c.angle === 0) && c.angle === props.current.angle && c.stops?.join(',') === props.current.stops?.join(','))
      )
    })
  );

  function selectColor(color) {
    props.onSelect(color);
  }

  function tooltipText(color, group) {
    if (group.key === SURFACE_EDITOR_COLOR_TYPES.solid.key) {
      return `${color.id} (${color.color})`;
    }

    if (group.key === SURFACE_EDITOR_COLOR_TYPES.linearGradient.key) {
      const angle = color.angle ?? '';
      const stops = Array.isArray(color.stops) ? color.stops.join(',') : '';
      return `Linear gradient: ${angle}°, ${stops}`;
    }

    return `${color.id}`;
  }
</script>
